import { useQuery, useMutation } from '@apollo/client'
import { useState, useEffect, useRef } from 'react'
import { Card, Table, Spinner, Form, Badge, Button, Modal, OverlayTrigger, Tooltip, Nav } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { CREATE_UPDATE_ORGANIZATION, CREATE_UPDATE_ORGANIZATION_STR } from '../../gql/mutations/organizationMutations'
import { useUploadFile } from '../../hooks/useUploadFile'
import { prependBackendUrl } from '../../gql/client'
import { PhoneProviderActions } from '../../components/telephony/PhoneProviderActions'
import { OrganizationSettingsTab } from './components/OrganizationSettingsTab'
import { OrganizationGeneralForm } from './components/OrganizationGeneralForm'
import { useGetUser } from '../../store/userStore'

const EMPTY_ORG = {
    name: '', owner: '', email: '', phone: '', website: '',
    streetAddress: '', city: '', country: 'Estados Unidos', state: '', postalCode: '',
}

export const OrganizationsPage = () => {
    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const { data, loading, refetch } = useQuery(GET_ALL_ORGANIZATIONS, { variables: { first: 100 } })
    const [createUpdateOrg] = useMutation(CREATE_UPDATE_ORGANIZATION)
    const [search, setSearch] = useState('')
    const [showArchived, setShowArchived] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editOrg, setEditOrg] = useState<any>(null)
    const [form, setForm] = useState<any>({ ...EMPTY_ORG })
    const [saving, setSaving] = useState(false)
    const [modalTab, setModalTab] = useState<'general' | 'settings'>('general')

    // Logo upload state
    const [logoPreview, setLogoPreview] = useState<string | null>(null)
    const [selectedLogo, setSelectedLogo] = useState<File | null>(null)
    const [logoLoadError, setLogoLoadError] = useState(false)
    const [uploadFile, { data: uploadData, loading: uploadingLogo, error: uploadError }] = useUploadFile<any>()
    const logoInputRef = useRef<HTMLInputElement>(null)

    // Template logo upload state
    const [tplLogoPreview, setTplLogoPreview] = useState<string | null>(null)
    const [selectedTplLogo, setSelectedTplLogo] = useState<File | null>(null)
    const [tplLogoLoadError, setTplLogoLoadError] = useState(false)
    const [uploadTplFile, { data: uploadTplData, loading: uploadingTplLogo, error: uploadTplError }] = useUploadFile<any>()
    const tplLogoInputRef = useRef<HTMLInputElement>(null)

    // Archive confirmation state (two-step)
    const [archiveOrg, setArchiveOrg] = useState<any>(null)
    const [showArchiveStep1, setShowArchiveStep1] = useState(false)
    const [showArchiveStep2, setShowArchiveStep2] = useState(false)
    const [archiving, setArchiving] = useState(false)

    const organizations = data?.allOrganizations?.edges?.map((e: any) => e.node) || []
    const filtered = organizations
        .filter((org: any) => showArchived ? org.isActive === false : org.isActive !== false)
        .filter((org: any) =>
            org.name?.toLowerCase().includes(search.toLowerCase()) ||
            org.owner?.toLowerCase().includes(search.toLowerCase()) ||
            org.email?.toLowerCase().includes(search.toLowerCase())
        )

    const activeCount = organizations.filter((o: any) => o.isActive !== false).length
    const archivedCount = organizations.filter((o: any) => o.isActive === false).length

    const openCreate = () => {
        setEditOrg(null)
        setForm({ ...EMPTY_ORG })
        setLogoPreview(null)
        setSelectedLogo(null)
        setLogoLoadError(false)
        setTplLogoPreview(null)
        setSelectedTplLogo(null)
        setTplLogoLoadError(false)
        setModalTab('general')
        setShowModal(true)
    }

    const openEdit = (org: any) => {
        setEditOrg(org)
        setForm({
            name: org.name || '',
            owner: org.owner || '',
            email: org.email || '',
            phone: org.phone || '',
            website: org.website || '',
            streetAddress: org.streetAddress || '',
            city: org.city || '',
            country: org.country || '',
            state: org.state || '',
            postalCode: org.postalCode || '',
        })
        setLogoPreview(null)
        setSelectedLogo(null)
        setLogoLoadError(false)
        setTplLogoPreview(null)
        setSelectedTplLogo(null)
        setTplLogoLoadError(false)
        setModalTab('general')
        setShowModal(true)
    }

    // Regular admins only ever see their own organization (backend already scopes the query),
    // so open its edit modal directly instead of showing a multi-org table.
    const myOrg = !isSuperAdmin ? organizations[0] : null
    const [autoOpened, setAutoOpened] = useState(false)
    useEffect(() => {
        if (!isSuperAdmin && myOrg && !autoOpened && !showModal) {
            setAutoOpened(true)
            openEdit(myOrg)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin, myOrg?.id, autoOpened])

    const handleChange = (field: string, value: string) => {
        setForm((prev: any) => ({ ...prev, [field]: value }))
    }

    const handleSave = async () => {
        if (!form.name || !form.owner || !form.email || !form.phone) {
            toast.warning('Please fill all required fields (Name, Owner, Email, Phone)')
            return
        }
        setSaving(true)
        try {
            const variables: any = {
                name: form.name,
                owner: form.owner,
                email: form.email,
                phone: form.phone,
                website: form.website || '',
                streetAddress: form.streetAddress,
                city: form.city,
                country: form.country,
                state: form.state || '',
                postalCode: form.postalCode,
                isActive: true,
            }
            if (editOrg) variables.id = editOrg.id

            const res = await createUpdateOrg({ variables })
            const errors = res.data?.organizationCreateupdateMutation?.errors
            if (errors && errors.length > 0) {
                const msgs = errors.map((e: any) => `${e.field}: ${e.messages.join(', ')}`).join('\n')
                toast.error(`Validation errors:\n${msgs}`)
            } else {
                toast.success(editOrg ? 'Organization updated successfully' : 'Organization created successfully')
                setShowModal(false)
                refetch()
            }
        } catch (err: any) {
            toast.error(err.message || 'Error saving organization')
        } finally {
            setSaving(false)
        }
    }

    // Logo file selection handler
    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setSelectedLogo(file)
            setLogoLoadError(false)
            const reader = new FileReader()
            reader.onloadend = () => setLogoPreview(reader.result as string)
            reader.readAsDataURL(file)
        }
    }

    // Upload logo via multipart request
    const handleLogoUpload = (orgId: string) => {
        if (!selectedLogo || !orgId) return
        const formdata = new FormData()
        formdata.append('0', selectedLogo, selectedLogo.name)

        const map = { '0': ['variables.logo'] }
        const variables: any = {
            id: orgId,
            name: form.name,
            owner: form.owner,
            email: form.email,
            phone: form.phone,
            website: form.website || '',
            streetAddress: form.streetAddress,
            city: form.city,
            country: form.country,
            state: form.state || '',
            postalCode: form.postalCode,
            isActive: true,
            logo: null,
        }

        formdata.append('map', JSON.stringify(map))
        formdata.append('operations', JSON.stringify({
            query: CREATE_UPDATE_ORGANIZATION_STR,
            variables,
        }))
        uploadFile(formdata)
    }

    // Handle upload result
    useEffect(() => {
        if (!uploadData) return
        const org = uploadData?.organizationCreateupdateMutation?.organization
        if (org?.id) {
            toast.success('Logo updated successfully')
            setSelectedLogo(null)
            setLogoPreview(null)
            setLogoLoadError(false)
            refetch()
        }
        const errors = uploadData?.organizationCreateupdateMutation?.errors
        if (errors?.length > 0) {
            toast.error(`Logo error: ${errors[0]?.messages?.join(', ') || 'Unknown error'}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadData])

    useEffect(() => {
        if (uploadError) toast.error('Error uploading logo')
    }, [uploadError])

    // Template logo file selection handler
    const handleTplLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setSelectedTplLogo(file)
            setTplLogoLoadError(false)
            const reader = new FileReader()
            reader.onloadend = () => setTplLogoPreview(reader.result as string)
            reader.readAsDataURL(file)
        }
    }

    // Upload template logo via multipart request
    const handleTplLogoUpload = (orgId: string) => {
        if (!selectedTplLogo || !orgId) return
        const formdata = new FormData()
        formdata.append('0', selectedTplLogo, selectedTplLogo.name)

        const map = { '0': ['variables.templateLogo'] }
        const variables: any = {
            id: orgId,
            name: form.name,
            owner: form.owner,
            email: form.email,
            phone: form.phone,
            website: form.website || '',
            streetAddress: form.streetAddress,
            city: form.city,
            country: form.country,
            state: form.state || '',
            postalCode: form.postalCode,
            isActive: true,
            templateLogo: null,
        }

        formdata.append('map', JSON.stringify(map))
        formdata.append('operations', JSON.stringify({
            query: CREATE_UPDATE_ORGANIZATION_STR,
            variables,
        }))
        uploadTplFile(formdata)
    }

    // Handle template logo upload result
    useEffect(() => {
        if (!uploadTplData) return
        const org = uploadTplData?.organizationCreateupdateMutation?.organization
        if (org?.id) {
            toast.success('Template logo updated successfully')
            setSelectedTplLogo(null)
            setTplLogoPreview(null)
            setTplLogoLoadError(false)
            refetch()
        }
        const errors = uploadTplData?.organizationCreateupdateMutation?.errors
        if (errors?.length > 0) {
            toast.error(`Template logo error: ${errors[0]?.messages?.join(', ') || 'Unknown error'}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadTplData])

    useEffect(() => {
        if (uploadTplError) toast.error('Error uploading template logo')
    }, [uploadTplError])

    // Step 1: open first confirmation modal
    const openArchiveConfirm = (org: any) => {
        setArchiveOrg(org)
        setShowArchiveStep1(true)
    }

    // Step 1 → Step 2: close first, open second
    const proceedToStep2 = () => {
        setShowArchiveStep1(false)
        setShowArchiveStep2(true)
    }

    // Step 2: execute the archive
    const executeArchive = async () => {
        if (!archiveOrg) return
        setArchiving(true)
        try {
            await createUpdateOrg({
                variables: {
                    id: archiveOrg.id,
                    name: archiveOrg.name,
                    owner: archiveOrg.owner,
                    email: archiveOrg.email,
                    phone: archiveOrg.phone,
                    website: archiveOrg.website || '',
                    streetAddress: archiveOrg.streetAddress,
                    city: archiveOrg.city,
                    country: archiveOrg.country,
                    state: archiveOrg.state || '',
                    postalCode: archiveOrg.postalCode,
                    isActive: false,
                },
            })
            toast.success('Organization archived successfully')
            setShowArchiveStep2(false)
            setArchiveOrg(null)
            refetch()
        } catch (err: any) {
            toast.error(err.message || 'Error archiving organization')
        } finally {
            setArchiving(false)
        }
    }

    const cancelArchive = () => {
        setShowArchiveStep1(false)
        setShowArchiveStep2(false)
        setArchiveOrg(null)
    }

    if (!isSuperAdmin) {
        return (
            <div className="container-xxl py-5">
                <div className="d-flex justify-content-between align-items-center mb-6">
                    <div>
                        <h1 className="fs-2 fw-bold text-gray-900 mb-1">My Organization</h1>
                        <p className="text-muted fs-6 mb-0">Manage your organization's details</p>
                    </div>
                    {myOrg && (
                        <Button variant="primary" onClick={() => openEdit(myOrg)} className="px-5">
                            <i className="bi bi-pencil me-1"></i>
                            Edit Organization
                        </Button>
                    )}
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center py-10">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : myOrg ? (
                    <Card className="shadow-sm">
                        <Card.Body className="d-flex align-items-center gap-4 p-5">
                            {myOrg.logo ? (
                                <img
                                    src={prependBackendUrl(myOrg.logo)}
                                    alt={myOrg.name}
                                    style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8 }}
                                />
                            ) : (
                                <div
                                    className="bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center rounded"
                                    style={{ width: 56, height: 56, fontSize: 20 }}
                                >
                                    {myOrg.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                            )}
                            <div>
                                <div className="fw-semibold fs-4 text-gray-800">{myOrg.name}</div>
                                <div className="text-muted fs-7">{myOrg.owner}</div>
                                <div className="text-muted fs-7">{myOrg.email}</div>
                            </div>
                        </Card.Body>
                    </Card>
                ) : (
                    <p className="text-muted">No organization found for your account.</p>
                )}

                {/* Create/Edit Modal */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold">Edit Organization</Modal.Title>
                    </Modal.Header>
                    {editOrg && (
                        <Nav variant="tabs" activeKey={modalTab} onSelect={(k) => setModalTab((k as 'general' | 'settings') || 'general')} className="px-4">
                            <Nav.Item>
                                <Nav.Link eventKey="general">General</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="settings">Settings</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    )}
                    {editOrg && modalTab === 'settings' ? (
                        <Modal.Body className="pt-4">
                            <OrganizationSettingsTab organizationId={editOrg.id} />
                        </Modal.Body>
                    ) : (
                        <Modal.Body className="pt-4">
                            <OrganizationGeneralForm
                                editOrg={editOrg}
                                form={form}
                                handleChange={handleChange}
                                logoPreview={logoPreview}
                                setLogoPreview={setLogoPreview}
                                selectedLogo={selectedLogo}
                                setSelectedLogo={setSelectedLogo}
                                logoLoadError={logoLoadError}
                                setLogoLoadError={setLogoLoadError}
                                logoInputRef={logoInputRef}
                                handleLogoSelect={handleLogoSelect}
                                handleLogoUpload={handleLogoUpload}
                                uploadingLogo={uploadingLogo}
                                tplLogoPreview={tplLogoPreview}
                                setTplLogoPreview={setTplLogoPreview}
                                selectedTplLogo={selectedTplLogo}
                                setSelectedTplLogo={setSelectedTplLogo}
                                tplLogoLoadError={tplLogoLoadError}
                                setTplLogoLoadError={setTplLogoLoadError}
                                tplLogoInputRef={tplLogoInputRef}
                                handleTplLogoSelect={handleTplLogoSelect}
                                handleTplLogoUpload={handleTplLogoUpload}
                                uploadingTplLogo={uploadingTplLogo}
                            />
                        </Modal.Body>
                    )}
                    <Modal.Footer className="border-0 pt-0">
                        {editOrg && modalTab === 'settings' ? (
                            <Button variant="light" onClick={() => setShowModal(false)}>
                                Close
                            </Button>
                        ) : (
                            <>
                            <Button variant="light" onClick={() => setShowModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSave} disabled={saving} className="px-6">
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            </div>
        )
    }

    return (
        <div className="container-xxl py-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fs-2 fw-bold text-gray-900 mb-1">Organizations</h1>
                    <p className="text-muted fs-6 mb-0">Manage all organizations in the platform</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex gap-2">
                        <Badge
                            bg={!showArchived ? 'primary' : 'light'}
                            text={!showArchived ? 'white' : 'dark'}
                            className="fs-7 px-3 py-2 cursor-pointer border"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowArchived(false)}
                        >
                            Active ({activeCount})
                        </Badge>
                        <Badge
                            bg={showArchived ? 'warning' : 'light'}
                            text="dark"
                            className="fs-7 px-3 py-2 cursor-pointer border"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowArchived(true)}
                        >
                            Archived ({archivedCount})
                        </Badge>
                    </div>
                    <Button variant="primary" onClick={openCreate} className="px-5">
                        <i className="bi bi-plus-lg me-1"></i>
                        New Organization
                    </Button>
                </div>
            </div>

            {/* Table Card */}
            <Card className="shadow-sm">
                <Card.Header className="border-bottom bg-white py-4">
                    <Form.Control
                        type="search"
                        placeholder="Search by name, owner, or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="mw-400px"
                    />
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center py-10">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="fw-bold text-gray-600 ps-5">Name</th>
                                    <th className="fw-bold text-gray-600">Owner</th>
                                    <th className="fw-bold text-gray-600">Email</th>
                                    <th className="fw-bold text-gray-600">Phone</th>
                                    <th className="fw-bold text-gray-600">Location</th>
                                    <th className="fw-bold text-gray-600 text-end pe-5" style={{ width: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-10 fs-5">
                                            {showArchived ? 'No archived organizations' : 'No organizations found'}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((org: any) => (
                                        <tr key={org.id}>
                                            <td className="ps-5">
                                                <div className="d-flex align-items-center gap-3">
                                                    {org.logo ? (
                                                        <img
                                                            src={prependBackendUrl(org.logo)}
                                                            alt={org.name}
                                                            style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6 }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center rounded"
                                                            style={{ width: 36, height: 36, fontSize: 15 }}
                                                        >
                                                            {org.name?.charAt(0)?.toUpperCase() || '?'}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="fw-semibold text-gray-800 d-block">{org.name}</span>
                                                        {org.website && (
                                                            <a href={org.website} target="_blank" rel="noreferrer" className="text-muted fs-8 text-hover-primary">
                                                                {org.website.replace(/^https?:\/\//, '')}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-gray-700">{org.owner || '—'}</td>
                                            <td className="text-gray-700">
                                                <a href={`mailto:${org.email}`} className="text-hover-primary">{org.email || '—'}</a>
                                            </td>
                                            <td className="text-gray-700">
                                                <PhoneProviderActions phone={org.phone} emptyText='—' />
                                            </td>
                                            <td className="text-gray-600 fs-7">
                                                {[org.city, org.state, org.country].filter(Boolean).join(', ') || '—'}
                                            </td>
                                            <td className="text-end pe-5">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            className="btn-icon"
                                                            onClick={() => openEdit(org)}
                                                        >
                                                            <i className="bi bi-pencil fs-6 text-primary"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger overlay={<Tooltip>Archive</Tooltip>}>
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            className="btn-icon"
                                                            onClick={() => openArchiveConfirm(org)}
                                                        >
                                                            <i className="bi bi-archive text-warning fs-6"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        {editOrg ? 'Edit Organization' : 'New Organization'}
                    </Modal.Title>
                </Modal.Header>
                {editOrg && (
                    <Nav variant="tabs" activeKey={modalTab} onSelect={(k) => setModalTab((k as 'general' | 'settings') || 'general')} className="px-4">
                        <Nav.Item>
                            <Nav.Link eventKey="general">General</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="settings">Settings</Nav.Link>
                        </Nav.Item>
                    </Nav>
                )}
                {editOrg && modalTab === 'settings' ? (
                    <Modal.Body className="pt-4">
                        <OrganizationSettingsTab organizationId={editOrg.id} />
                    </Modal.Body>
                ) : (
                <Modal.Body className="pt-4">
                    <OrganizationGeneralForm
                        editOrg={editOrg}
                        form={form}
                        handleChange={handleChange}
                        logoPreview={logoPreview}
                        setLogoPreview={setLogoPreview}
                        selectedLogo={selectedLogo}
                        setSelectedLogo={setSelectedLogo}
                        logoLoadError={logoLoadError}
                        setLogoLoadError={setLogoLoadError}
                        logoInputRef={logoInputRef}
                        handleLogoSelect={handleLogoSelect}
                        handleLogoUpload={handleLogoUpload}
                        uploadingLogo={uploadingLogo}
                        tplLogoPreview={tplLogoPreview}
                        setTplLogoPreview={setTplLogoPreview}
                        selectedTplLogo={selectedTplLogo}
                        setSelectedTplLogo={setSelectedTplLogo}
                        tplLogoLoadError={tplLogoLoadError}
                        setTplLogoLoadError={setTplLogoLoadError}
                        tplLogoInputRef={tplLogoInputRef}
                        handleTplLogoSelect={handleTplLogoSelect}
                        handleTplLogoUpload={handleTplLogoUpload}
                        uploadingTplLogo={uploadingTplLogo}
                    />
                </Modal.Body>
                )}
                <Modal.Footer className="border-0 pt-0">
                    {editOrg && modalTab === 'settings' ? (
                        <Button variant="light" onClick={() => setShowModal(false)}>
                            Close
                        </Button>
                    ) : (
                    <>
                    <Button variant="light" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving} className="px-6">
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            editOrg ? 'Save Changes' : 'Create Organization'
                        )}
                    </Button>
                    </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Archive Step 1: Initial Confirmation */}
            <Modal show={showArchiveStep1} onHide={cancelArchive} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                        Archive Organization
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-gray-700 fs-5 mb-3">
                        Are you sure you want to archive <strong>"{archiveOrg?.name}"</strong>?
                    </p>
                    <div className="bg-light-warning rounded p-4">
                        <div className="d-flex align-items-start gap-2">
                            <i className="bi bi-info-circle text-warning fs-4 mt-1"></i>
                            <div>
                                <p className="fw-semibold text-gray-800 mb-1">What happens when you archive:</p>
                                <ul className="text-gray-600 mb-0 ps-3">
                                    <li>The organization will no longer appear in active lists</li>
                                    <li>Users assigned to this organization may lose access</li>
                                    <li>All associated configurations will be disabled</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={cancelArchive}>Cancel</Button>
                    <Button variant="warning" onClick={proceedToStep2} className="px-5">
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Archive Step 2: Final Danger Confirmation */}
            <Modal show={showArchiveStep2} onHide={cancelArchive} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold text-danger">
                        <i className="bi bi-exclamation-octagon text-danger me-2"></i>
                        Confirm Archive
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center py-3">
                        <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{ width: 64, height: 64 }}>
                            <i className="bi bi-archive text-danger fs-1"></i>
                        </div>
                        <h4 className="fw-bold text-gray-900 mb-2">This action cannot be undone</h4>
                        <p className="text-gray-600 mb-0">
                            You are about to permanently archive <strong>"{archiveOrg?.name}"</strong>.
                            This action is irreversible and cannot be reverted.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="light" onClick={cancelArchive} className="px-5">Cancel</Button>
                    <Button variant="danger" onClick={executeArchive} disabled={archiving} className="px-5">
                        {archiving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Archiving...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-archive me-1"></i>
                                Archive Permanently
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
